import { useState, type ComponentType } from 'react'
import {
  IconeAfastar,
  IconeAproximar,
  IconeChevronBaixo,
  IconeEnquadrarCena,
  IconeEnquadrarSelecao,
  IconeOrbitar,
  IconeRestaurarCamera,
  IconeVistaFrontal,
  IconeVistaIsometrica,
  IconeVistaLateral,
  IconeVistaSuperior,
  type PropsIcone,
} from '../icones'
import type { TipoComandoCamera, VistaNomeada } from '../../visualizacao3d/camera/comandosCamera'
import type { ModoLayout } from './useModoLayout'

interface Props {
  camera: {
    executar: (tipo: TipoComandoCamera) => void
    vistaAtiva: VistaNomeada | null
  }
  temSelecao: boolean
  modoLayout: ModoLayout
  /** Table top: a cena é montada livremente e pode não caber no quadro padrão (sobretudo no celular, em pé). */
  comEnquadrarCena?: boolean
}

const VISTAS: Array<{ tipo: VistaNomeada; rotulo: string; atalho: string; Icone: ComponentType<PropsIcone> }> = [
  { tipo: 'vistaIsometrica', rotulo: 'Isométrica (3D)', atalho: '4', Icone: IconeVistaIsometrica },
  { tipo: 'vistaFrontal', rotulo: 'Frontal', atalho: '1', Icone: IconeVistaFrontal },
  { tipo: 'vistaLateral', rotulo: 'Lateral', atalho: '2', Icone: IconeVistaLateral },
  { tipo: 'vistaSuperior', rotulo: 'Superior', atalho: '3', Icone: IconeVistaSuperior },
]

/**
 * Dock translúcido flutuante de câmera no Viewport 3D (estilo AutoCAD ViewCube / Fusion 360).
 *
 * Fica em overlay sobre a cena 3D, liberando a barra lateral esquerda para ferramentas
 * operacionais e de manipulação.
 */
export function DockCamera3D({ camera, temSelecao, modoLayout, comEnquadrarCena = false }: Props) {
  const [expandidoMobile, setExpandidoMobile] = useState(false)
  const isMobile = modoLayout === 'mobile'

  if (isMobile) {
    return (
      <aside className="dock-camera-3d dock-camera-3d--mobile" aria-label="Controles de Câmera">
        <button
          type="button"
          className="dock-camera-3d__btn-gatilho"
          onClick={() => setExpandidoMobile(!expandidoMobile)}
          aria-expanded={expandidoMobile}
          title="Controles de Visualização 3D"
        >
          <IconeOrbitar tamanho={16} />
          <IconeChevronBaixo tamanho={12} className={expandidoMobile ? 'girado-180' : ''} />
        </button>

        {expandidoMobile && (
          <div className="dock-camera-3d__menu-mobile">
            <div className="dock-camera-3d__grupo">
              {VISTAS.map(({ tipo, rotulo, Icone }) => (
                <button
                  key={tipo}
                  type="button"
                  className={`dock-camera-3d__btn${camera.vistaAtiva === tipo ? ' dock-camera-3d__btn--ativo' : ''}`}
                  onClick={() => {
                    camera.executar(tipo)
                    setExpandidoMobile(false)
                  }}
                  title={rotulo}
                >
                  <Icone tamanho={15} />
                  <span>{rotulo}</span>
                </button>
              ))}
            </div>

            <div className="dock-camera-3d__divisor" />

            <div className="dock-camera-3d__grupo">
              <button
                type="button"
                className="dock-camera-3d__btn"
                onClick={() => {
                  camera.executar('enquadrarSelecao')
                  setExpandidoMobile(false)
                }}
                disabled={!temSelecao}
                title={temSelecao ? 'Enquadrar Seleção' : 'Selecione algo para enquadrar'}
              >
                <IconeEnquadrarSelecao tamanho={15} />
                <span>Enquadrar</span>
              </button>
              {comEnquadrarCena && (
                <button
                  type="button"
                  className="dock-camera-3d__btn"
                  onClick={() => {
                    camera.executar('enquadrarCena')
                    setExpandidoMobile(false)
                  }}
                  title="Ver a cena toda"
                >
                  <IconeEnquadrarCena tamanho={15} />
                  <span>Cena toda</span>
                </button>
              )}
              <button
                type="button"
                className="dock-camera-3d__btn"
                onClick={() => {
                  camera.executar('liberarCamera')
                  setExpandidoMobile(false)
                }}
                title="Câmera Livre / Órbita Livre"
              >
                <IconeOrbitar tamanho={15} />
                <span>Livre</span>
              </button>
              <button
                type="button"
                className="dock-camera-3d__btn"
                onClick={() => {
                  camera.executar('restaurar')
                  setExpandidoMobile(false)
                }}
                title="Restaurar Câmera"
              >
                <IconeRestaurarCamera tamanho={15} />
                <span>Padrão</span>
              </button>
            </div>
          </div>
        )}
      </aside>
    )
  }

  return (
    <aside className="dock-camera-3d" aria-label="Controles de Câmera 3D">
      {/* Vistas pré-definidas */}
      <div className="dock-camera-3d__grupo" role="group" aria-label="Vistas ortogonais e isométrica">
        {VISTAS.map(({ tipo, rotulo, atalho, Icone }) => {
          const ativo = camera.vistaAtiva === tipo
          return (
            <button
              key={tipo}
              type="button"
              className={`dock-camera-3d__btn${ativo ? ' dock-camera-3d__btn--ativo' : ''}`}
              onClick={() => camera.executar(tipo)}
              aria-pressed={ativo}
              title={`${rotulo} [${atalho}]`}
            >
              <Icone tamanho={16} />
              <span className="dock-camera-3d__badge-atalho">{atalho}</span>
            </button>
          )
        })}
      </div>

      <div className="dock-camera-3d__divisor" />

      {/* Zoom e Enquadramento */}
      <div className="dock-camera-3d__grupo" role="group" aria-label="Ajuste de zoom e foco">
        <button
          type="button"
          className="dock-camera-3d__btn"
          onClick={() => camera.executar('aproximar')}
          title="Aproximar Zoom [+]"
        >
          <IconeAproximar tamanho={16} />
        </button>
        <button
          type="button"
          className="dock-camera-3d__btn"
          onClick={() => camera.executar('afastar')}
          title="Afastar Zoom [-]"
        >
          <IconeAfastar tamanho={16} />
        </button>
        <button
          type="button"
          className="dock-camera-3d__btn"
          onClick={() => camera.executar('enquadrarSelecao')}
          disabled={!temSelecao}
          title={temSelecao ? 'Enquadrar Seleção [F]' : 'Selecione algo para enquadrar'}
        >
          <IconeEnquadrarSelecao tamanho={16} />
        </button>
        {comEnquadrarCena && (
          <button
            type="button"
            className="dock-camera-3d__btn"
            onClick={() => camera.executar('enquadrarCena')}
            title="Ver a cena toda"
          >
            <IconeEnquadrarCena tamanho={16} />
          </button>
        )}
        <button
          type="button"
          className="dock-camera-3d__btn"
          onClick={() => camera.executar('liberarCamera')}
          title="Câmera Livre / Órbita Livre"
        >
          <IconeOrbitar tamanho={16} />
        </button>
        <button
          type="button"
          className="dock-camera-3d__btn"
          onClick={() => camera.executar('restaurar')}
          title="Restaurar Visão Padrão [Home]"
        >
          <IconeRestaurarCamera tamanho={16} />
        </button>
      </div>
    </aside>
  )
}
